/* eslint-disable react/prop-types */
import { Badge, Nav, Tab, Table, Form } from "react-bootstrap";
import { getMenuLanguages } from "../../../constants/menuLanguages";

import PageTitle from "../../layouts/PageTitle";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";
import config from "../../../../config.json";
import "../../../i18n";
import { useTranslation } from "react-i18next";
import {
  IMAGE_PICKER_ACCEPT,
  validateImageFile,
} from "../../../utils/imageValidation";
import NutritionValuesEditor from "../shared/NutritionValuesEditor";
import ProductBadgesEditor from "../shared/ProductBadgesEditor";
import { normalizeNutritionValuesForSave } from "../../../constants/nutritionOptions";
import { normalizeProductBadgesForSave } from "../../../constants/productBadgeOptions";
import DualPriceFields, { ExtraSizeDualPriceInputs } from "./DualPriceFields";
import { parseOptionalPrice } from "../../../utils/currency";

const AddProduct = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const languages = getMenuLanguages(t);
  const [activeLang, setActiveLang] = useState(languages[0].code);
  const [productDetails, setProductDetails] = useState(
    languages.reduce((acc, lang) => {
      acc[lang.code] = {
        productTitle: "",
        productDescription: "",
        // productHistory: "",
      };
      return acc;
    }, {})
  );

  const [formData, setFormData] = useState({
    price: 0,
    secondaryPrice: "",
    rate: 0,
    isMultipleSize: false,
    extraSizes: [{ size: "", finalPrice: "", secondaryFinalPrice: "" }],
    photos: [],
  });

  const [files, setFiles] = useState(Array(4).fill(null));
  const [imageUrls, setImageUrls] = useState(Array(4).fill(null));
  const [imageDisabled, setImageDisabled] = useState(false);
  const [productBadges, setProductBadges] = useState([]);
  const [nutritionValues, setNutritionValues] = useState([]);
  const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState("");

  const stripHtml = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  // Gérer les changements de langues
  const handleTabSelect = (key) => setActiveLang(key);

  // Gérer les changements des champs par langue
  const handleLangInputChange = (lang, field, value) => {
    setProductDetails({
      ...productDetails,
      [lang]: {
        ...productDetails[lang],
        [field]: value,
      },
    });
  };

  // Gérer les changements des tailles et prix supplémentaires
  const handleSizeChange = (index, value) => {
    const updatedSizes = [...formData.extraSizes];
    updatedSizes[index].finalPrice = value;
    setFormData({ ...formData, extraSizes: updatedSizes });
  };

  const handleAddTag = () => {
    if (currentTag && !tags.includes(currentTag)) {
      setTags([...tags, currentTag]);
      setCurrentTag("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const normalizedProductBadges = normalizeProductBadgesForSave(productBadges);
  const normalizedNutritionValues = normalizeNutritionValuesForSave(nutritionValues);

  // Gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Construction des extraSizes valides
    const extraSizes = formData.extraSizes
      .filter(
        (size) =>
          size.size ||
          parseOptionalPrice(size.finalPrice) != null ||
          parseOptionalPrice(size.secondaryFinalPrice) != null
      )
      .map((size) => ({
        size: size.size,
        finalPrice: parseOptionalPrice(size.finalPrice) ?? 0,
        secondaryFinalPrice: parseOptionalPrice(size.secondaryFinalPrice),
      }));

    // Construction des détails du produit
    const productDetailsToSend = Object.entries(productDetails).map(
      ([lang, details]) => ({
        language: lang,
        productTitle: stripHtml(details.productTitle?.trim() ?? "") || "",
        productDescription: stripHtml(details.productDescription?.trim() ?? "") || "",
      })
    );

    const hasTitle = productDetailsToSend.some(
      (d) => String(d.productTitle || "").trim().length > 0
    );
    if (!hasTitle) {
      swal(
        t("warningTitle"),
        t("productTitleRequired") || "Product title is required.",
        "warning"
      );
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append(
      "data",
      JSON.stringify({
        price: parseOptionalPrice(formData.price) ?? 0,
        secondaryPrice: parseOptionalPrice(formData.secondaryPrice),
        rate: parseFloat(formData.rate),
        haveMultipleSize: formData.isMultipleSize || false,
        extraSizes,
        productDetails: productDetailsToSend,
        tags: tags,
        productBadges: normalizedProductBadges,
        productBadge: "",
        nutritionValues: normalizedNutritionValues,
        imageDisabled,
        // preparationTime,
      })
    );

    // Ajouter les fichiers au FormData
    files.forEach((file) => {
      if (file) formDataToSend.append("file", file);
    });

    // Envoi des données à l'API
    try {
      const response = await axios.post(
        `${config.dev}/v2/products/create`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
          },
        }
      );
      swal({
        title: t("successTitle"),
        text: t("productCreated"),
        icon: "success",
        timer: 1000, // durée en millisecondes (ici 2 secondes)
        buttons: false, // pour ne pas afficher de bouton "OK"
      });
      setTimeout(() => {
        navigate("/products");
      }, 1000);
      console.log(response.data);
    } catch (error) {
      console.error(
        "Erreur lors de l'ajout du produit :",
        error.response?.data || error.message
      );
      alert(t("failedAddProduct"));
    }
  };

  // Gérer les fichiers (images)
  const handleFileChange = (event, index) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const { isValid, message } = validateImageFile(selectedFile, 5);
    if (!isValid) {
      swal("Warning", message, "warning");
      event.target.value = "";
      return;
    }

    const updatedFiles = [...files];
    const updatedUrls = [...imageUrls];

    updatedFiles[index] = selectedFile;
    updatedUrls[index] = selectedFile
      ? URL.createObjectURL(selectedFile)
      : null;

    setFiles(updatedFiles);
    setImageUrls(updatedUrls);
  };

  const handleRemoveImage = (index) => {
    setImageUrls((prevUrls) => prevUrls.filter((_, i) => i !== index));
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const navigateTo = (e) => {
    e.preventDefault();
    navigate("/products");
  };

  return (
    <>
      <div className="row">
        <div className="col-xl-4 col-lg-4 col-md-4 col-sm-4">
          <PageTitle activeMenu={t("addProduct")} motherMenu={t("kitchen")} />
        </div>
        <div className="col-xl-2 col-lg-2 col-md-2 col-sm-2 offset-xl-6 offset-lg-6 offset-md-6 offset-sm-6">
          <button onClick={(e) => navigateTo(e)} className="btn btn-primary">
            {t("productsList")}
          </button>
        </div>
      </div>
      <div className="row">
        <div className="col-lg-3 col-md-8 col-sm-12 mt-4">
          {/* <h4 className="mt-4">{t("uploadPhoto")}</h4> */}
          <div className="row">
            <div className="col-12 col-md-6 col-lg-6">
              <div className="mt-2">
                <label style={{ fontSize: 13, cursor: "pointer" }}>
                  <i className="fa fa-camera" style={{ fontSize: 16 }} />{" "}
                  {t("uploadPhoto")}
                  <hr />
                  <input
                    hidden
                    type="file"
                    className="form-control"
                    accept={IMAGE_PICKER_ACCEPT}
                    onChange={(e) => handleFileChange(e, 0)} // index 0 pour la seule image
                  />
                </label>
              </div>
              <div className="d-flex justify-content-center">
                {imageUrls[0] && (
                  <div
                    className="image-preview"
                    style={{
                      position: "relative",
                      width: "100px",
                      height: "100px",
                    }}
                  >
                    <img
                      src={imageUrls[0]}
                      alt="Preview"
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                      }}
                    />
                    <button
                      onClick={() => handleRemoveImage(0)}
                      style={{
                        position: "relative",
                        top: "-65px",
                        right: "-38px",
                        background: "#ff4d4f",
                        border: "none",
                        borderRadius: "50%",
                        color: "#fff",
                        width: "25px",
                        height: "25px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                      }}
                      title="Supprimer l'image"
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </div>
                )}
              </div>
              <Form.Check
                type="switch"
                id="add-product-image-disabled"
                className="mt-3"
                label={t("disableImage")}
                checked={imageDisabled}
                onChange={(e) => setImageDisabled(e.target.checked)}
              />
            </div>
          </div>
        </div>

        <div className="col-lg-9">
          <Tab.Container
            onSelect={handleTabSelect}
            defaultActiveKey={languages[0].code}
          >
            <Nav as="ul" className="nav-tabs">
              {languages.map((lang) => (
                <Nav.Item as="li" key={lang.code}>
                  <Nav.Link eventKey={lang.code}>
                    <img width={25} src={lang.flag} alt={lang.label} />
                    &nbsp; <b className="pt-2">{lang.label}</b>
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
            <Tab.Content className="pt-4">
              {languages.map((lang) => (
                <Tab.Pane eventKey={lang.code} key={lang.code}>
                  <div className="form-group">
                    <h4>
                      {t("title")}{" "}
                      <span className="text-danger" aria-hidden="true">
                        *
                      </span>
                    </h4>
                    <input
                      type="text"
                      className="form-control"
                      value={productDetails[lang.code].productTitle}
                      required
                      aria-required="true"
                      onChange={(e) =>
                        handleLangInputChange(
                          lang.code,
                          "productTitle",
                          e.target.value
                        )
                      }
                    />
                  </div>

                  <div className="form-group mt-3">
                    <h4 className="">{t("description")}</h4>
                    <textarea
                      type="text"
                      className="form-control"
                      value={productDetails[lang.code].productDescription}
                      onChange={(e) =>
                        handleLangInputChange(
                          lang.code,
                          "productDescription",
                          e.target.value
                        )
                      }
                    />
                  </div>

                  <DualPriceFields
                    price={formData.price}
                    secondaryPrice={formData.secondaryPrice}
                    disabled={formData.isMultipleSize}
                    onPriceChange={(value) =>
                      setFormData({ ...formData, price: value })
                    }
                    onSecondaryPriceChange={(value) =>
                      setFormData({ ...formData, secondaryPrice: value })
                    }
                  />

                  {/* <h4 style={{ marginTop: 30 }}>
                    Preparation Time (In minutes)
                  </h4>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.preparationTime}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData,
                        preparationTime:
                          value === "" ? "" : Math.max(0, parseInt(value)),
                      });
                    }}
                    min="0"
                    placeholder="Durée de préparation"
                  />*/}
                  <div className="form-check mt-4">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={formData.isMultipleSize}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          isMultipleSize: !formData.isMultipleSize,
                        })
                      }
                    />
                    <label className="form-check-label pt-1">
                      {t("haveMultipleSizes")}
                    </label>
                  </div>

                  {formData.isMultipleSize && (
                    <>
                      <h4 className="mt-3">{t("extraSizes")}</h4>
                      {formData.extraSizes.map((size, index) => (
                        <div key={index} className="d-flex mb-2">
                          <input
                            type="text"
                            className="form-control me-2"
                            placeholder="Size"
                            value={size.size}
                            onChange={(e) => {
                              const newSizes = [...formData.extraSizes];
                              newSizes[index].size = e.target.value;
                              setFormData({
                                ...formData,
                                extraSizes: newSizes,
                              });
                            }}
                          />
                          <ExtraSizeDualPriceInputs
                            size={size}
                            onFinalPriceChange={(value) => {
                              const newSizes = [...formData.extraSizes];
                              newSizes[index].finalPrice = value;
                              setFormData({
                                ...formData,
                                extraSizes: newSizes,
                              });
                            }}
                            onSecondaryFinalPriceChange={(value) => {
                              const newSizes = [...formData.extraSizes];
                              newSizes[index].secondaryFinalPrice = value;
                              setFormData({
                                ...formData,
                                extraSizes: newSizes,
                              });
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-danger ms-2"
                            onClick={() => {
                              const newSizes = formData.extraSizes.filter(
                                (_, i) => i !== index
                              );
                              setFormData({
                                ...formData,
                                extraSizes: newSizes,
                              });
                            }}
                          >
                            <i className="fa fa-trash" />
                          </button>
                        </div>
                      ))}
                      <div className="d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-primary mt-2"
                          onClick={() =>
                            setFormData({
                              ...formData,
                              extraSizes: [
                                ...formData.extraSizes,
                                { size: "", finalPrice: 0, secondaryFinalPrice: "" },
                              ],
                            })
                          }
                        >
                          <i className="fa fa-plus" />
                        </button>
                      </div>
                    </>
                  )}
                </Tab.Pane>
              ))}
            </Tab.Content>
          </Tab.Container>

          <ProductBadgesEditor
            values={productBadges}
            onChange={setProductBadges}
          />

          <NutritionValuesEditor
            values={nutritionValues}
            onChange={setNutritionValues}
          />

          <div className="form-group mt-4">
            <h4>{t("tags") || "Tags"}</h4>
            <div className="d-flex">
              <input
                type="text"
                className="form-control"
                value={currentTag}
                onChange={(e) => setCurrentTag(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), handleAddTag())
                }
                placeholder="Add a tag..."
              />
              <button
                type="button"
                className="btn btn-primary ms-2"
                onClick={handleAddTag}
              >
                Add
              </button>
            </div>
            <div className="mt-2">
              {tags.map((tag, index) => (
                <Badge
                  key={index}
                  bg="info"
                  className="me-1 p-2 text-white"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleRemoveTag(tag)}
                >
                  {tag} &times;
                </Badge>
              ))}
            </div>
          </div>

          <div className="d-flex justify-content-end">
            <button
              onClick={handleSubmit}
              className="btn btn-primary w-25 mt-4"
            >
              {t("addProduct")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddProduct;
